/** Apply a nutrition-V2 menu template, or leave it for the builder.
 *
 *  Scope is deliberately one call. Building a real menu is `MenuBuilderV2Page`'s job — a food-level
 *  editor does not belong in a creation wizard — and there is no macro-targets shortcut here on
 *  purpose: targets are the *output* of the calculator in the builder, and writing them onto a
 *  `DETAILED_PLAN` menu with no meals produces a menu the builder immediately argues with. */

import { Box, CircularProgress } from "@mui/material";
import { ContentPaste as TemplateIcon } from "@mui/icons-material";
import { t, macro } from "../../../dashboard/coach/coachTokens";
import type { MenuTemplateV2 } from "../../../nutrition/v2/model/nutritionV2Model";
import StepShell from "../StepShell";
import type { NutritionValues } from "../wizardReducer";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { POINTS, energyText, macroFigure } from "../../../nutrition/v2/ui/nutritionUnits";
import type { NutritionDisplayMode, PointsMacroKey } from "../../../nutrition/v2/ui/nutritionUnits";

/** A template's energy: `2000 קל׳` as before, or `20 נק׳` for a points company. */
const energyCopy = (kcal: number, mode: NutritionDisplayMode): string =>
  mode === POINTS ? energyText(kcal, mode) : `${kcal} ${macro.calories.short}`;

/** A macro: grams (`150ח׳`), or that macro's points in their place (`6ח׳`). */
const macroCopy = (key: PointsMacroKey, grams: number, mode: NutritionDisplayMode): string =>
  `${mode === POINTS ? macroFigure(key, grams, mode).text : grams}${macro[key].short}`;

interface NutritionStepProps {
  values: NutritionValues;
  error: string | null;
  templates: MenuTemplateV2[];
  loading: boolean;
  onChange: (values: Partial<NutritionValues>) => void;
}

const NutritionStep = ({
  values,
  error,
  templates,
  loading,
  onChange,
}: NutritionStepProps) => {
  const mode = useNutritionDisplayMode();
  return (
    <StepShell
      title="תפריט תזונה"
      description="החלת תבנית מעתיקה את כל הארוחות והיעדים שלה למתאמן. אפשר לדלג ולבנות תפריט מלא אחר כך מעמוד התזונה של הלקוח."
      error={error}
    >
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: t.t2, fontSize: "13px" }}>
          <CircularProgress size={16} sx={{ color: t.green }} />
          טוען תבניות תפריט…
        </Box>
      ) : (
        <>
          {templates.length === 0 ? (
            <Box sx={{ fontSize: "13px", color: t.t2 }}>
              אין תבניות תפריט בחברה. אפשר לדלג ולבנות תפריט ישירות בבונה התפריטים.
            </Box>
          ) : (
            <Box
              role="radiogroup"
              aria-label="תבניות תפריט"
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                border: `1px solid ${t.border}`,
                borderRadius: t.rs,
              }}
            >
              {templates.map(template => {
                const selected = values.templateId === template.id;
                const targets = template.targets;
                return (
                  <Box
                    key={template.id}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => onChange({ choice: "template", templateId: template.id })}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      width: "100%",
                      padding: "11px 14px",
                      border: "none",
                      borderBottom: `1px solid ${t.borderLight}`,
                      backgroundColor: selected ? t.greenLight : t.white,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      textAlign: "start",
                      "&:last-of-type": { borderBottom: "none" },
                      "&:hover": { backgroundColor: selected ? t.greenLight : t.bg },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: "13.5px",
                          fontWeight: selected ? 700 : 600,
                          color: selected ? t.greenText : t.text,
                        }}
                      >
                        <TemplateIcon
                          sx={{ fontSize: 15, color: t.t3, marginInlineEnd: "6px", verticalAlign: "-2px" }}
                        />
                        {template.name}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "10px",
                          marginTop: "4px",
                          fontSize: "11.5px",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {targets?.calories ? (
                          <Box component="span" sx={{ color: t.t2 }}>
                            {energyCopy(targets.calories, mode)}
                          </Box>
                        ) : null}
                        {targets?.protein ? (
                          <Box component="span" sx={{ color: macro.protein.text }}>
                            {macroCopy("protein", targets.protein, mode)}
                          </Box>
                        ) : null}
                        {targets?.carbs ? (
                          <Box component="span" sx={{ color: macro.carbs.text }}>
                            {macroCopy("carbs", targets.carbs, mode)}
                          </Box>
                        ) : null}
                        {targets?.fats ? (
                          <Box component="span" sx={{ color: macro.fats.text }}>
                            {macroCopy("fats", targets.fats, mode)}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

        </>
      )}
    </StepShell>
  );
};

export default NutritionStep;
